'use client';

import React, { useState } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';

interface SectionCardProps {
  label: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
  headerAction?: React.ReactNode;
  'data-testid': string;
}

const SectionCard = ({
  label,
  defaultOpen = false,
  children,
  headerAction,
  'data-testid': testId,
}: SectionCardProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <section data-testid={testId} aria-label={label}>
      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
        <div
          role="button"
          tabIndex={0}
          className="px-5 py-4 flex items-center justify-between gap-3 cursor-pointer transition-colors hover:bg-neutral-50 focus-visible:outline-none"
          onClick={() => setIsOpen((prev) => !prev)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsOpen((prev) => !prev);
            }
          }}
          aria-expanded={isOpen}
          aria-label={label}
        >
          <div className="flex flex-col gap-1 min-w-0">
            <span className="text-sm font-bold text-primary">{label}</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {headerAction && (
              <div
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                {headerAction}
              </div>
            )}
            <Icon
              icon={faChevronDown}
              className={`text-xs text-secondary transition-transform duration-300 ${
                isOpen ? 'rotate-0' : '-rotate-90'
              }`}
              aria-hidden="true"
            />
          </div>
        </div>
        {isOpen && (
          <div className="bg-neutral-50 px-3 py-3 border-t border-neutral-200 flex flex-col gap-4">
            {children}
          </div>
        )}
      </div>
    </section>
  );
};

export default SectionCard;
